import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import {
  ROAD_CLASS_WIDTH,
  className,
  project,
  type County,
  type PlaceKind,
  type Projection,
  type RoadClass,
} from '@hotlist/shared';
import {
  PLACE_LABEL_COLOR,
  ROAD_COLOR,
  ROAD_DRAW_ORDER,
  ROAD_MIN_PIXELS,
} from './theme.ts';
import type { Viewport } from './viewport.ts';

/** Label weight by settlement size, so a county reads at a glance. */
const PLACE_LABEL_SIZE: Record<PlaceKind, number> = {
  city: 14,
  town: 13,
  village: 11,
  hamlet: 9,
};

const PLACE_LABEL_ALPHA: Record<PlaceKind, number> = {
  city: 0.95,
  town: 0.95,
  village: 0.78,
  hamlet: 0.55,
};

/**
 * The map.
 *
 * Roads never move, so their geometry is built once and then only panned.
 * Rebuilding on zoom is necessary rather than lazy: a road drawn at its real
 * width is a third of a pixel when the whole county is on screen, and a map you
 * cannot see is not a map.
 *
 * ## What is built, and why it is not everything
 *
 * This used to hand Pixi **all 53,481 edges** on every rebuild, unculled, at
 * every zoom. On a desktop that is merely wasteful. On a phone it is fatal: a
 * pinch crosses a dozen rebuild bands, each one re-tessellating the whole
 * county, and the tab dies. It was reported as "I zoomed out a ways and then it
 * just started lagging and the page died", which is exactly what that is.
 *
 * Two things fix it, and they cover opposite ends of the zoom range.
 *
 * **Culling**, for when you are zoomed in. Only edges near the view are built,
 * with a generous margin so ordinary panning does not trigger a rebuild. At
 * street level this is a few hundred edges instead of fifty-three thousand.
 *
 * **Level of detail**, for when you are zoomed out -- where culling does
 * nothing, because everything is in view. 68% of this county's edges are
 * residential street. Eleven meters of residential road at county zoom is a
 * *fifth of a pixel*, floored up to 0.7px, so those 36,472 segments are not
 * information: they are a grey mat laid over the thing you are trying to read,
 * and they cost more than everything else put together. A class drops out once
 * its real width falls below what a pixel can show.
 *
 * The arterials never drop, whatever the zoom. They are the skeleton that makes
 * a county recognisable as itself, and a map that dissolves when you pull back
 * is not a map either.
 */

/**
 * How much of a pixel a road has to be worth before it is drawn.
 *
 * Below this it is not a road on the screen, it is a tint.
 */
const LOD_PIXELS = 0.55;

/** Classes drawn at every zoom, because they are the county's shape. */
const ALWAYS_DRAWN = new Set<RoadClass>(['motorway', 'trunk', 'primary']);

/** How far past the view to build, as a fraction of it, so panning is free. */
const BUILD_MARGIN = 0.6;
export class RoadLayer {
  readonly container = new Container();
  readonly labelContainer = new Container();

  private readonly graphics = new Map<RoadClass, Graphics>();
  private readonly labels: { text: Text; x: number; y: number }[] = [];
  private readonly projectedX: Float64Array;
  private readonly projectedY: Float64Array;
  private builtAtScale = 0;
  /** The region the current geometry covers, so panning inside it is free. */
  private builtBounds: { minX: number; minY: number; maxX: number; maxY: number } | null = null;

  private readonly county: County;
  private readonly projection: Projection;

  constructor(county: County, projection: Projection) {
    this.county = county;
    this.projection = projection;
    this.projectedX = new Float64Array(county.nodeCount);
    this.projectedY = new Float64Array(county.nodeCount);
    for (let n = 0; n < county.nodeCount; n++) {
      const p = project(projection, county.nodeX[n] as number, county.nodeY[n] as number);
      this.projectedX[n] = p.sx;
      this.projectedY[n] = p.sy;
    }

    for (const cls of ROAD_DRAW_ORDER) {
      const graphic = new Graphics();
      this.graphics.set(cls, graphic);
      this.container.addChild(graphic);
    }

    this.buildLabels();
  }

  projectedNodeX(node: number): number {
    return this.projectedX[node] as number;
  }

  projectedNodeY(node: number): number {
    return this.projectedY[node] as number;
  }

  /**
   * Redraw if the zoom has moved enough to matter, or if the view has left what
   * was built.
   *
   * A 12% band keeps this off the hot path during a wheel gesture without
   * letting stroke widths drift visibly out of step with the zoom, and the
   * build margin keeps ordinary panning off it entirely.
   */
  update(viewport: Viewport): void {
    const scale = viewport.scale;
    const view = viewport.visibleBounds(0);
    const zoomHeld = this.builtAtScale > 0 && Math.abs(scale / this.builtAtScale - 1) < 0.12;
    const built = this.builtBounds;
    const inside =
      built !== null &&
      view.minX >= built.minX &&
      view.maxX <= built.maxX &&
      view.minY >= built.minY &&
      view.maxY <= built.maxY;

    if (zoomHeld && inside) {
      this.place(viewport);
      return;
    }

    const padX = (view.maxX - view.minX) * BUILD_MARGIN;
    const padY = (view.maxY - view.minY) * BUILD_MARGIN;
    const region = {
      minX: view.minX - padX,
      minY: view.minY - padY,
      maxX: view.maxX + padX,
      maxY: view.maxY + padY,
    };
    this.builtBounds = region;
    this.builtAtScale = scale;
    this.draw(scale, region);
    this.place(viewport);
  }

  /**
   * Whether a class is worth drawing at this zoom.
   *
   * Its real width in pixels, against what a pixel can actually show. The
   * arterials are exempt because they are the county's shape, not its detail.
   */
  private worthDrawing(cls: RoadClass, scale: number): boolean {
    if (ALWAYS_DRAWN.has(cls)) return true;
    return (ROAD_CLASS_WIDTH[cls] ?? 8) * scale >= LOD_PIXELS;
  }

  /**
   * How strongly to draw the roads.
   *
   * At full strength over aerial imagery the map covers the photograph it is
   * sitting on: you can see the roads twice and the ground not at all, which
   * makes turning imagery on pointless. Stepped back when there is a picture
   * underneath, because the picture is already showing you where the roads are
   * and what the map adds there is the *names* and the classes.
   */
  setUnderlay(on: boolean): void {
    this.container.alpha = on ? 0.42 : 1;
  }

  private place(viewport: Viewport): void {
    this.container.scale.set(viewport.scale);
    this.container.position.set(viewport.toScreenX(0), viewport.toScreenY(0));

    for (const label of this.labels) {
      label.text.position.set(viewport.toScreenX(label.x), viewport.toScreenY(label.y));
    }
  }

  private draw(
    scale: number,
    bounds: { minX: number; minY: number; maxX: number; maxY: number },
  ): void {
    const county = this.county;

    // Bucket the edges by class so each class is one path and one stroke,
    // rather than 5,000 separate stroke calls. Anything outside the built
    // region, or too small to see at this zoom, never reaches the bucket.
    const byClass = new Map<RoadClass, number[]>();
    for (let e = 0; e < county.edgeCount; e++) {
      const cls = className(county.edgeClass[e] as number);
      if (!this.worthDrawing(cls, scale)) continue;

      const a = county.edgeA[e] as number;
      const b = county.edgeB[e] as number;
      const ax = this.projectedX[a] as number;
      const ay = this.projectedY[a] as number;
      const bx = this.projectedX[b] as number;
      const by = this.projectedY[b] as number;
      // Segment bounding box against the built region. A cheap reject, and the
      // false positives it lets through are edges straddling a corner.
      if (Math.max(ax, bx) < bounds.minX || Math.min(ax, bx) > bounds.maxX) continue;
      if (Math.max(ay, by) < bounds.minY || Math.min(ay, by) > bounds.maxY) continue;

      let list = byClass.get(cls);
      if (!list) {
        list = [];
        byClass.set(cls, list);
      }
      list.push(e);
    }

    for (const cls of ROAD_DRAW_ORDER) {
      const graphic = this.graphics.get(cls);
      if (!graphic) continue;
      graphic.clear();
      const edges = byClass.get(cls);
      if (!edges || edges.length === 0) continue;

      for (const e of edges) {
        const a = county.edgeA[e] as number;
        const b = county.edgeB[e] as number;
        graphic.moveTo(this.projectedX[a] as number, this.projectedY[a] as number);
        graphic.lineTo(this.projectedX[b] as number, this.projectedY[b] as number);
      }

      // Widths are in projected units because the container carries the zoom,
      // so the pixel floor is divided back out here.
      const world = ROAD_CLASS_WIDTH[cls] ?? 8;
      const floor = (ROAD_MIN_PIXELS[cls] ?? 1) / scale;
      graphic.stroke({
        width: Math.max(world, floor),
        color: ROAD_COLOR[cls],
        cap: 'round',
        join: 'round',
        alignment: 0.5,
      });
    }
  }

  private buildLabels(): void {
    for (const place of this.county.places) {
      const p = project(this.projection, place.x, place.y);
      const style = new TextStyle({
        fontFamily: 'ui-monospace, Consolas, monospace',
        fontSize: PLACE_LABEL_SIZE[place.kind],
        fill: PLACE_LABEL_COLOR,
        letterSpacing: place.kind === 'city' || place.kind === 'town' ? 3 : 1.5,
      });
      const text = new Text({ text: place.name.toUpperCase(), style });
      text.anchor.set(0.5, 1.6);
      text.resolution = 2;
      text.alpha = PLACE_LABEL_ALPHA[place.kind];
      this.labelContainer.addChild(text);
      this.labels.push({ text, x: p.sx, y: p.sy });
    }
  }

}
